import { debounce } from "@mui/material";
import { SEARCH_FIELD_LIMIT } from "@sinch/core/config/globals";
import { useLazyQueryHandler } from "@sinch/hooks/useLazyQueryHandler";
import { useOptionsByValues } from "@sinch/hooks/utilities/useOptionsByValues";
import { mapToOptions, Option } from "@sinch/utils/option";
import { ensureArray } from "ramda-adjunct";
import React, { useMemo } from "react";
import { AutocompleteInput } from "../../../form/AutocompleteInput/AutocompleteInput";
import { ContractTypeListItem, ContractTypesList } from "./queries.graphql";
import { ContractTypeListItemFragment, ContractTypesListQuery, ContractTypesListQueryVariables } from "./queries.types";
import { AutocompleteInputProps, AutocompleteOptions } from "@sinch/components/form/AutocompleteInput/types";

export const ContractTypesSelect = <
  Multiple extends boolean | undefined = undefined,
  DisableClearable extends boolean | undefined = undefined,
  FreeSolo extends boolean | undefined = undefined,
>(
  props: Omit<AutocompleteInputProps<AutocompleteOptions, Multiple, DisableClearable, FreeSolo>, "ref" | "options">
) => {

  const { data, dispatch, loading } = useLazyQueryHandler<ContractTypesListQuery, ContractTypesListQueryVariables>(
    ContractTypesList
  );

  const valuesOptions = useOptionsByValues<ContractTypeListItemFragment>({
    values: ensureArray(props.value ?? []).map(String),
    fragment: ContractTypeListItem,
  });
  const options: Option[] = useMemo(
    () => mapToOptions("id", "name")([...valuesOptions, ...(data?.contractTypes || [])]),
    [data?.contractTypes, valuesOptions]
  );

  const debounceDispatch = useMemo(() => debounce(dispatch, 200), [dispatch]);

  const handleSearch = (search: string) => {
    // an empty search loads the first records, so focusing the input opens a dropdown with contract types
    debounceDispatch({ name: search, limit: SEARCH_FIELD_LIMIT });
  };

  return (
    <AutocompleteInput<AutocompleteOptions, Multiple, DisableClearable, FreeSolo>
      openOnFocus
      size="small"
      {...props}
      filterSelectedOptions
      limitCount={SEARCH_FIELD_LIMIT}
      loading={loading}
      onSearch={handleSearch}
      options={options}
    />
  );
};
